import { Box, Checkbox, Chip, Divider, List, ListItem, ListItemButton, ListItemText, Stack, Typography } from "@mui/material";
import { useHookFormContext } from "../../../../../utils/hooks/useHookFormContext";
import { getAgendaStatusColor, getAgendaStatusText } from "../../../../accept-invitation-form/helper";
import { AgendaListProps } from "./type";
import moment from "moment-timezone";
import ScrollIndicatorWrapper from "../../../../../atoms/scroll-indicator-wrapper";

const AgendaList = ({
  agendas,
  selectedAgendaId,
  setSelectedAgendaId,
}: AgendaListProps) => {
  const { watch, setValue } = useHookFormContext();
  const selectedAgendas = watch("selected_agendas") || [];

  const handleAgendaSelection = (agendaId: number, checked: boolean) => {
    let newSelection = [...selectedAgendas];

    if (checked) {
      if (!newSelection.includes(agendaId)) {
        newSelection.push(agendaId);
      }
    } else {
      newSelection = newSelection.filter(id => id !== agendaId);
    }

    setValue("selected_agendas", newSelection);
  };

  const handleAgendaPreview = (agendaId: number) => {
    setSelectedAgendaId(selectedAgendaId === agendaId ? null : agendaId);
  };

  return (
    <ScrollIndicatorWrapper sx={{
      overflow: 'auto',
      flex: 1,
    }}>
      <List sx={{ p: 0 }}>
        {agendas.map((agenda) => {
          const questionCount = Array.isArray(agenda?.questions)
            ? agenda.questions.length
            : 0;
          const isSelected = selectedAgendas.includes(agenda.id);
          const isPreviewActive = selectedAgendaId === agenda.id;
          const peUpdated = agenda.pe_agenda_updated_at ? moment(agenda.pe_agenda_updated_at) : null;
          const respondedOn = agenda.agenda_responded_on ? moment(agenda.agenda_responded_on) : null;
          const agendaUpdated = agenda.agenda_updated_at ? moment(agenda.agenda_updated_at) : null;

          let latestMoment = agendaUpdated;

          if (peUpdated && respondedOn) {
            latestMoment = peUpdated.isAfter(respondedOn) ? peUpdated : respondedOn;
          } else if (peUpdated) {
            latestMoment = peUpdated;
          } else if (respondedOn) {
            latestMoment = respondedOn;
          }

          const relativeTime = latestMoment ? latestMoment.fromNow() : "No timestamp";
          return (
            <>
              <ListItem key={agenda.id} disablePadding>
                <ListItemButton
                  selected={isPreviewActive}
                  onClick={(e) => {
                    // prevent checkbox click from triggering preview toggle
                    const target = e.target as HTMLElement;
                    if (target.closest('input[type="checkbox"]')) return;
                    handleAgendaPreview(agenda.id);
                  }}
                  sx={{ width: '100%', display: 'flex', alignItems: 'center' }}
                >
                  <Checkbox
                    checked={isSelected}
                    onChange={(e) => {
                      handleAgendaSelection(agenda.id, e.target.checked);
                    }}
                    sx={{ mr: 1 }}
                  />
                  <ListItemText
                    primary={
                      <Stack direction="row" justifyContent={'space-between'} alignItems="center" mt={0.5}>
                        <Typography
                          fontSize="14px"
                          color={isSelected ? 'primary' : 'text.primary'}
                          fontWeight={500}
                          noWrap
                        >
                          {agenda.title || "No Title Provided"}
                        </Typography>
                        <Typography fontSize="10px" color="text.secondary" whiteSpace="nowrap">
                          {relativeTime}
                        </Typography>
                      </Stack>
                    }
                    secondary={
                      <Stack direction="row" justifyContent={'space-between'} alignItems="center" mt={0.5}>
                        <Typography variant="caption" color="text.secondary">
                          {questionCount} questions
                        </Typography>
                        <Chip
                          label={getAgendaStatusText(agenda.status)}
                          color={getAgendaStatusColor(agenda.status)}
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: '10px', height: '20px' }}
                        />
                      </Stack>
                    }
                  />
                </ListItemButton>
              </ListItem>
              <Divider variant="middle" />
            </>
          );
        })}
      </List>
    </ScrollIndicatorWrapper>
  );
};

export default AgendaList;